import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { ShieldCheck } from 'lucide-react'
import { useAuth } from '../lib/auth'
import Button from '../components/ui/Button'
import { TextField, PasswordField } from '../components/ui/Field'

/**
 * Platform administrator sign-in — the only entry point into this app.
 *
 * No organisation field — there is nothing to pick, and no cross-link to the
 * tenant portal's login either: they are different deployments now, not two
 * routes of one app.
 */
export default function Login() {
  const { signIn, isAuthenticated } = useAuth()
  const navigate = useNavigate()

  const [form, setForm] = useState({ email: '', password: '' })
  const [error, setError] = useState(null)
  const [fieldErrors, setFieldErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)

  if (isAuthenticated) return <Navigate to="/" replace />

  async function onSubmit(event) {
    event.preventDefault()

    setError(null)
    setFieldErrors({})
    setSubmitting(true)

    try {
      const admin = await signIn(form)

      navigate(admin.must_change_password ? '/change-password' : '/', { replace: true })
    } catch (err) {
      setError(err.message)
      setFieldErrors(err.fields ?? {})
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-[var(--color-surface)] px-5 py-10 sm:px-8">
      <div className="w-full max-w-sm">
        <div className="mb-7">
          <ShieldCheck size={28} className="mb-4" style={{ color: 'var(--color-accent)' }} aria-hidden="true" />

          <h1 className="font-display text-xl font-semibold text-[var(--color-ink-900)]">
            Platform administration
          </h1>
          <p className="mt-1 text-sm text-[var(--color-ink-500)]">
            Manage client organisations.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          {error && (
            <div
              role="alert"
              className="rounded-md border border-[var(--color-danger)] px-3 py-2.5 text-sm"
              style={{ backgroundColor: 'var(--color-danger-bg)', color: 'var(--color-danger)' }}
            >
              {error}
            </div>
          )}

          <TextField
            label="Email"
            type="email"
            required
            value={form.email}
            onChange={(event) => setForm({ ...form, email: event.target.value })}
            autoComplete="username"
            inputMode="email"
            autoFocus
            error={fieldErrors.email?.[0]}
          />

          <PasswordField
            label="Password"
            required
            value={form.password}
            onChange={(event) => setForm({ ...form, password: event.target.value })}
            autoComplete="current-password"
            error={fieldErrors.password?.[0]}
          />

          <Button type="submit" variant="primary" size="lg" loading={submitting} className="w-full">
            Sign in
          </Button>
        </form>
      </div>
    </div>
  )
}
